/**
 * Gift with purchase — FREE Papi Tee on qualifying carts over $50.
 *
 * Pairs with snippets/gwp-free-tee.liquid (UI + JSON config) and the
 * automatic discount "Free Papi Tee (orders over $50)" in Shopify admin.
 *
 * Uses event delegation on document so the handlers survive the theme's
 * cart section re-renders (shapes:cart:update swaps the cart DOM).
 * The click listener is bound in the CAPTURE phase because the theme's
 * cart drawer stops click propagation in the bubble phase.
 */
(() => {
  const getConfig = () => {
    const el = document.querySelector('script[data-gwp-config]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  };

  const refreshCartUI = () =>
    document.dispatchEvent(new CustomEvent('theme:update:cart'));

  /* ---- Picker interactions ---- */

  const onClick = async (e) => {
    const designBtn = e.target.closest('[data-gwp-design]');
    if (designBtn) {
      const root = designBtn.closest('[data-gwp]');
      root.querySelectorAll('[data-gwp-design]').forEach((b) => {
        b.setAttribute('aria-pressed', String(b === designBtn));
      });
      root.querySelectorAll('[data-gwp-variants]').forEach((panel) => {
        panel.classList.toggle(
          'hidden',
          panel.dataset.gwpVariants !== designBtn.dataset.gwpDesign
        );
      });
      root.querySelectorAll('[data-gwp-variant]').forEach((b) => {
        b.setAttribute('aria-pressed', 'false');
      });
      root.querySelector('[data-gwp-add-wrap]').classList.remove('hidden');
      root.querySelector('[data-gwp-add]').disabled = true;
      delete root.dataset.gwpChosen;
      return;
    }

    const sizeBtn = e.target.closest('[data-gwp-variant]');
    if (sizeBtn) {
      const root = sizeBtn.closest('[data-gwp]');
      root.querySelectorAll('[data-gwp-variant]').forEach((b) => {
        b.setAttribute('aria-pressed', String(b === sizeBtn));
      });
      root.dataset.gwpChosen = sizeBtn.dataset.gwpVariant;
      root.querySelector('[data-gwp-add]').disabled = false;
      return;
    }

    // The theme's .push-btn wrapper receives the pointer events (its
    // __surface child is pointer-events: none), so match the wrapper
    // as well as the button and resolve the real button from the root.
    const addHit = e.target.closest('[data-gwp-add-wrap], [data-gwp-add]');
    if (addHit) {
      const root = addHit.closest('[data-gwp]');
      const addBtn = root.querySelector('[data-gwp-add]');
      const variantId = root.dataset.gwpChosen;
      if (!addBtn || !variantId || addBtn.disabled) return;
      addBtn.disabled = true;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: Number(variantId),
            quantity: 1,
            properties: { _gwp: '1' },
          }),
        });
        if (!res.ok) throw new Error(`gwp add failed (${res.status})`);
        refreshCartUI();
      } catch (err) {
        addBtn.disabled = false;
        const errEl = root.querySelector('[data-gwp-error]');
        if (errEl) {
          errEl.textContent =
            'Sorry — that one just sold out. Try another size.';
          errEl.classList.remove('hidden');
        }
      }
    }
  };

  document.addEventListener('click', onClick, true);

  /* ---- Guardian: remove the gift if the cart drops below the threshold ----
   *
   * Without this, a cart that falls under $50 keeps the tee but the
   * automatic discount stops applying, and the customer gets charged
   * full price at checkout. Only lines we added (property _gwp) are
   * ever removed — a tee the customer added themselves is left alone.
   */

  let guardianBusy = false;

  const guardian = async () => {
    const config = getConfig();
    if (!config || !config.enabled || guardianBusy) return;
    guardianBusy = true;
    try {
      const cart = await (await fetch('/cart.js')).json();
      const giftLines = cart.items.filter(
        (i) => i.properties && i.properties._gwp
      );
      if (!giftLines.length) return;
      const qualifying = cart.items
        .filter((i) => !config.productIds.includes(i.product_id))
        .reduce((sum, i) => sum + i.final_line_price, 0);
      if (qualifying >= config.thresholdCents) return;
      for (const line of giftLines) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 }),
        });
      }
      refreshCartUI();
    } catch (e) {
      console.error('[gwp]', e);
    } finally {
      guardianBusy = false;
    }
  };

  document.addEventListener('shapes:cart:update', guardian);
  document.addEventListener('theme:cart:change', guardian);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', guardian);
  } else {
    guardian();
  }
})();